import { Icon } from "@fluentui/react";
import { Form, Upload, UploadProps, notification, message, Select, Checkbox, UploadFile, Spin } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { memo, useEffect, useState } from "react";
import { useAppDispatch } from "../../../../redux/useAppDispatch";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/Store";
import { documentUpload, fetchTargetLanguageList, fetchUploadMetaData, resetTabState } from "../../../../redux/EngageTranslation/EngageTranslationAction";
import { getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import { getExtension } from "../../../../utils";
import Modal from "antd/es/modal/Modal";
import "./QuickUploadModal.css";

type FileUploadModalProps = {
  close: () => void;
  openState: boolean;
  open: () => void;
};
const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
  },
  wrapperCol: {
    xs: { span: 24 },
  },
};

const acceptFormat = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];

function arePropsEqual(oldProps: FileUploadModalProps, newProps: FileUploadModalProps) {
  return oldProps.openState === newProps.openState;
}
function QuickUploadModal(props: FileUploadModalProps) {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [form] = Form.useForm();
  const clearUploadForm = () => {
    form.resetFields();
    setFileList([]);
  };
  const dispatch = useAppDispatch();
  const uploadFormState = useSelector((state: RootState) => state.engageTranslationState.engageTranslateMainState.uploadFormMetaDataState);

  useEffect(() => {
    if (props.openState) {
      dispatch(fetchUploadMetaData());
    } else {
      clearUploadForm();
    }
  }, [props.openState]);

  const arrangeLanguagesAlphabetically = (languagesArray: Array<{ label: string; value: number; id: number }>) => {
    return [...languagesArray].sort((a: any, b: any) => a.label.localeCompare(b.label, "en", { sensitivity: "base" }));
  };

  const { Dragger } = Upload;
  function handleSourceLanguageChange(value: number) {
    dispatch(fetchTargetLanguageList(value));
    form.setFieldsValue({ targetLanguageId: null });
  }
  const uploadProps: UploadProps = {
    name: "file",
    multiple: false,
    // disabled: true,
    showUploadList: false,

    beforeUpload: (file) => {
      const isPDF = acceptFormat.includes(file.type);

      if (!isPDF) {
        return false;
      }
      setFileList((prev) => [file]);

      return false;
    },
  };

  const handleSubmission = async (values: any) => {
    dispatch(resetTabState("new"));
    values["uploadType"] = "quick";
    dispatch(documentUpload(values));
    props.close();
  };

  const QuickUploadModalContent = () => {
    const { Dragger } = Upload;
    return (
      <Spin spinning={uploadFormState.loading}>
        <div
          className="d-flex flex-column justify-content-between gap-4 h-100 needs-validation"
          id="fileUploadForm">
          <h4 className="m-0 p-0">Quick Upload Document</h4>
          <Form
            form={form}
            onFinish={(values) => {
              handleSubmission(values);
            }}
            scrollToFirstError
            {...formItemLayout}
            colon={false}>
            <div className="d-flex gap-5 w-100">
              <div className="w-50">
                <Form.Item
                  className="mt-2 mb-2"
                  name="clientId"
                  required
                  rules={[{ required: true, message: "Please provide Client Name." }]}
                  label="Client Name">
                  <Select options={uploadFormState.clientList} />
                </Form.Item>
                <Form.Item
                  className="mt-2 mb-2"
                  name={"sourceLanguageId"}
                  required
                  rules={[
                    {
                      required: true,
                      message: "Please provide Source Language.",
                    },
                  ]}
                  label="Source Language">
                  <Select
                    onChange={(value) => {
                      handleSourceLanguageChange(value);
                    }}
                    options={arrangeLanguagesAlphabetically(uploadFormState.sourceLanguageList)}
                  />
                </Form.Item>
                <Form.Item
                  className="mt-2 mb-2"
                  name={"targetLanguageId"}
                  required
                  rules={[
                    {
                      required: true,
                      message: "Please provide Output Language.",
                    },
                  ]}
                  label="Output Language">
                  <Select options={arrangeLanguagesAlphabetically(uploadFormState.targetLanguageList)} />
                </Form.Item>
              </div>
              <Form.Item
                name={"file"}
                className="w-100"
                wrapperCol={{ flex: 1 }}
                getValueFromEvent={(e) => {
                  return e.fileList[0];
                }}
                rules={[
                  {
                    validator: (rule, value, callback) => {
                      // Custom validation logic
                      if (value.size / 1024 / 1024 > 2000) {
                        callback("File size should be less than 2 GB");
                        // } else if (fileUploadArray[value.name] !== undefined) {
                        //   callback("File already exists");
                      } else if (!acceptFormat.includes(value.type)) {
                        callback("Please upload a PDF document");
                      } else if (rule.required) {
                        callback("Please upload the document");
                      } else {
                        callback();
                      }
                    },
                  },
                ]}>
                <Dragger
                  style={{ width: "100%" }}
                  height={248}
                  accept={".pdf, .doc, .docx"}
                  {...uploadProps}>
                  {fileList.length > 0 ? (
                    <div className="d-flex flex-column align-items-center">
                      <Icon
                        {...getFileTypeIconProps({
                          extension: getExtension(fileList[0].type),
                          size: 96,
                          imageFileType: "svg",
                        })}
                      />
                      {fileList[0].name}
                    </div>
                  ) : (
                    <div>
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                      </p>
                      <p className="ant-upload-text">Click or drag file to this area to upload</p>
                      <p className="ant-upload-hint">Support for single upload. Maximum 5 pages per document.</p>
                    </div>
                  )}
                </Dragger>
              </Form.Item>
            </div>
          </Form>
        </div>
        <div className="d-flex gap-4 mt-2 justify-content-between">
          <button
            className="button warning"
            onClick={() => clearUploadForm()}>
            Clear
          </button>
          <div className="d-flex gap-4">
            <button
              type="submit"
              onClick={() => {
                console.log("form submit click");
                form.submit();
              }}
              className="button">
              Ok
            </button>
            <button
              type="button"
              onClick={() => props.close()}
              className="button warning">
              Cancel
            </button>
          </div>
        </div>
      </Spin>
    );
  };
  return (
    <Modal
      centered
      width={1000}
      zIndex={1500}
      footer={[]}
      onCancel={() => props.close()}
      open={props.openState}>
      <QuickUploadModalContent />
    </Modal>
  );
}

export default memo(QuickUploadModal, arePropsEqual);
